import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import * as fromSearch from '@app/store/actions/search.action';
import { Router } from '@angular/router';
import * as algoliasearch from 'algoliasearch';
import { ProductsService } from '@vestiaire/api-sdk-angular';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { environment } from '@env/environment';
import { SearchIndex, SuggestionsIndex } from '@app/shared/components/catalog/services/instantsearch.service';
import { ResultsAlgoliaSearch } from '../../catalog/model/algolia.model';
import { LocaleService } from '@app/core/i18n';
import { SuperTrimPipe } from '@app/shared/pipes/super-trim';
import { Logger } from '@app/core/logging';

type PromiseResolve<T> = (value?: T | PromiseLike<T>) => void;
type PromiseReject = (error?: any) => void;

@Component({
  selector: 'vc-searchbar-content',
  templateUrl: './searchbar-content.component.html',
  styleUrls: ['./searchbar-content.component.scss']
})
export class SearchbarContentComponent implements OnInit, AfterViewInit {
  @Input()
  parentLocation: 'modal' | 'header';

  @Output()
  submitSearch: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('searchInput', { static: false })
  searchInput: ElementRef;

  public isSearchOpen = false;
  public searchValue = '';
  public isSearchActive = false;
  public mostPopular: any;
  public recentSearches: {}[];
  public indexNameSearch = SearchIndex.Default;
  public suggestionsResults = { brands: [], products: [] };

  private client: any;
  private superTrimPipe = new SuperTrimPipe();

  constructor(
    private router: Router,
    private platformService: PlatformService,
    private productService: ProductsService,
    private state: Store<IAppState>,
    private localeService: LocaleService,
    private logger: Logger
  ) {}

  ngOnInit() {
    if (this.platformService.isBrowser) {
      this.client = algoliasearch(environment.algoliaAppId, environment.algoliaApiKey);
      this.getMostPopular();
      this.getSuggestionsFromAlgolia();
    }
    this.getLastSearches();
    this.state.select('search').subscribe(
      resp => {
        this.searchValue = resp.searchValue;
      },
      () => {
        /* Silently catch exception */
      }
    );
    if (this.searchValue !== '') {
      this.isSearchActive = true;
    }
  }

  ngAfterViewInit(): void {
    this.focusSearchInput();
  }

  getLastSearches() {
    let lastSearches = [];
    this.state.select('search').subscribe(
      resp => {
        if (resp.lastSearches && Array.isArray(resp.lastSearches) && resp.lastSearches.length) {
          // With slice(0) the array is cloned and therefore not frozen anymore
          // @see https://davidwalsh.name/javascript-clone-array
          lastSearches = resp.lastSearches
            .slice(0)
            // Reverting array to slice its first elements
            .reverse()
            // We only keep the 5 most recent elements
            .slice(Math.max(lastSearches.length - 8))
            // Reverting again to keep the initial order
            .reverse();
        }

        this.recentSearches = lastSearches;
      },
      () => {
        /* Silently catch exception */
      }
    );
  }

  getSuggestionsFromAlgolia() {
    const analyticsTags = ['web'];

    const queries = [
      {
        indexName: SearchIndex.Brands,
        query: '',
        params: {
          hitsPerPage: 4,
          analyticsTags
        }
      },
      {
        indexName: this.searchSuggestionIndex(),
        query: '',
        params: {
          hitsPerPage: 5,
          analyticsTags
        }
      }
    ];

    this.getValueFromAlgolia(queries)
      .then((results: ReadonlyArray<ResultsAlgoliaSearch>) => {
        if (results.length > 1 && results[0].index === SearchIndex.Brands) {
          this.suggestionsResults.brands = results[0].hits;
          this.suggestionsResults.products = results[1].hits;
        } else {
          this.suggestionsResults.products = results[0].hits;
        }
      })
      .catch(err => {
        this.logger.error('[Error] Algolia Serach : ', err);
      });
  }

  getMostPopular() {
    const queries = [
      {
        indexName: this.searchSuggestionIndex(),
        query: '',
        params: {
          hitsPerPage: 8,
          analyticsTags: ['web']
        }
      }
    ];
    this.getValueFromAlgolia(queries)
      .then(results => {
        this.mostPopular = results[0].hits;
      })
      .catch(err => {
        this.logger.error('[Error] Algolia Serach : ', err);
      });
  }

  getValueFromAlgolia(queries) {
    queries.forEach(query => {
      query.query = this.searchValue;
    });
    return new Promise((resolve: PromiseResolve<[ResultsAlgoliaSearch]>, reject: PromiseReject): void => {
      this.client.search(queries, (err, content) => {
        if (err) {
          reject(err);
        } else {
          resolve(content.results);
        }
      });
    });
  }

  clearSearch() {
    this.searchInput.nativeElement.value = '';
    this.state.dispatch(new fromSearch.SetSearchValue(''));
    this.searchValue = '';
    this.focusSearchInput();
  }

  focusSearchInput() {
    if (this.platformService.isBrowser) {
      window.scrollTo(0, 0);
    }
    if (this.searchValue !== '') {
      this.getSuggestionsFromAlgolia();
    }
    this.searchInput && this.searchInput.nativeElement ? this.searchInput.nativeElement.focus() : (function() {})();
  }

  sendQueryToSearch(query) {
    const searchQuery = this.superTrimPipe.transform(query.searchValue);
    this.state.dispatch(new fromSearch.ManualSearch({ query: searchQuery }));

    if (searchQuery.match(/^\d{3,9}$/)) {
      this.productService.getProduct(searchQuery).subscribe(
        resp => {
          this.router.navigate([resp.data.path]);
        },
        error => {}
      );
    } else {
      this.router.navigate(['/search/', ''], { queryParams: { q: searchQuery } });
    }
    this.submitSearch.emit(this.searchValue);
  }

  listenComp(inputValue) {
    this.isSearchActive = Boolean(inputValue);
    this.state.dispatch(new fromSearch.SetSearchValue(inputValue));
    this.getSuggestionsFromAlgolia();
  }

  private setSearchbarValue(value: string = '') {
    this.state.dispatch({ type: fromSearch.SET_SEARCH_VALUE, payload: value });
    this.submitSearch.emit(this.searchValue);
  }

  private searchSuggestionIndex(): string {
    const lang = this.localeService.getLocale()['language'];
    switch (lang) {
      case 'en':
        return SuggestionsIndex.SuggestionsEN;
      case 'it':
        return SuggestionsIndex.SuggestionIT;
      case 'de':
        return SuggestionsIndex.SuggestionsDE;
      case 'es':
        return SuggestionsIndex.SuggestionsES;
      case 'fr':
        return SuggestionsIndex.SuggestionsFR;
      default:
        return SuggestionsIndex.SuggestionDefault;
    }
  }

  /**
   * Select a search suggestion.
   *
   * @param suggested Suggested query.
   * @param type type of suggestion
   */
  selectSearchSuggestion(
    suggested: string,
    type: 'popular' | 'recent' | 'suggestion product' | 'suggestion brand'
  ): void {
    const query = this.superTrimPipe.transform(suggested);
    this.setSearchbarValue(query);
    this.state.dispatch(
      new fromSearch.AutocompleteSearch({
        query,
        contentId: type,
        contentType: 'autocomplete'
      })
    );
  }
}
